import type {Cell, Row} from '@tanstack/react-table';
import {flexRender} from '@tanstack/react-table';
import {DotIcon} from 'lucide-react';

import {Typography} from '@/components/ui/typography';

import type {ResidentWithOccupiedRoom} from '../utils/getResidentsWithRoom';

export type ExportResidentsItemProps = {
  row: Row<ResidentWithOccupiedRoom>;
};

const ExportResidentsItem = ({row}: ExportResidentsItemProps) => {
  const allCells = row
    .getAllCells()
    .reduce<Record<string, Cell<ResidentWithOccupiedRoom, unknown>>>(
      (cells, cel) => ({
        ...cells,
        [cel.column.columnDef.id!]: cel,
      }),
      {}
    );

  const getCellRenderById = (
    cell?: Cell<ResidentWithOccupiedRoom, unknown>
  ) => {
    if (!cell) return;
    return flexRender(cell.column.columnDef.cell, cell.getContext());
  };

  const contactInfo = [
    allCells.contactEmail,
    allCells.contactPhoneNumber,
  ].filter(cell => cell.column.getIsVisible());
  const otherInfo = [
    allCells.personalNumber,
    allCells.dateOfBirth,
    allCells.accommodatedSince,
    allCells.occupiedRoom,
  ].filter(cell => cell.column.getIsVisible());

  const showContactInfo = !!contactInfo.length;
  const showOtherInfo = !!otherInfo.length;
  const showInfo = showContactInfo || showOtherInfo;

  return (
    <div className="flex flex-row items-center gap-5 overflow-hidden py-3">
      {getCellRenderById(allCells?.['select-col'])}
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center gap-2">
          {getCellRenderById(allCells?.avatar)}
          <Typography variant="h5" className="flex flex-row flex-wrap gap-1">
            <span>{getCellRenderById(allCells?.firstName)}</span>
            {row.getIsExpanded() &&
              allCells?.middleName?.column?.getIsVisible() && (
                <span>{getCellRenderById(allCells?.middleName)}</span>
              )}
            <span>{getCellRenderById(allCells?.lastName)}</span>
          </Typography>
        </div>
        {row.getIsExpanded() && showInfo && (
          <div className="flex flex-col gap-1">
            {showContactInfo && (
              <div className="flex flex-row flex-wrap items-center gap-2">
                {contactInfo.map(cell => (
                  <>
                    <Typography className="truncate text-xs">
                      {getCellRenderById(cell)}
                    </Typography>
                    <DotIcon size={14} className="last:hidden max-sm:hidden" />
                  </>
                ))}
              </div>
            )}
            {showOtherInfo && (
              <div className="flex flex-row flex-wrap items-center gap-2">
                {otherInfo.map(cell => (
                  <>
                    <Typography className="truncate text-xs">
                      <span className="text-gray-900">
                        {cell
                          .getContext()
                          .column?.columnDef?.header?.toString()}
                        :{' '}
                      </span>
                      {getCellRenderById(cell)}
                    </Typography>
                    <DotIcon size={14} className="last:hidden max-sm:hidden" />
                  </>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportResidentsItem;
